import { useEffect, useMemo, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useProjects } from '../hooks/useProjects';
import { ROLES } from '../../../shared/constant/roles';
import { getSchoolYearForDate } from '../../../shared/constant/schoolYears';
import { DISBURSED_STATUSES } from '../../../shared/constant/projectStatus';
import { confirmAction, showAlert } from '../../../shared/utils/dialogs';
import Pagination from '../../../shared/components/Pagination';
import ProjectFormModal from './ProjectFormModal';

const peso = (value) => `₱${Number(value).toLocaleString()}`;
const PAGE_SIZE = 10;

const statusStyles = {
  Planned: 'bg-gray-200 text-gray-600',
  Ongoing: 'bg-warning/10 text-warning-dark',
  Completed: 'bg-success/10 text-success',
};

function ProjectsPage() {
  const { role } = useAuth();
  const canDelete = role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN;
  const { projects, loading, error, create, update, remove } = useProjects();
  const [modalState, setModalState] = useState(null); // null | 'new' | project object
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [schoolYearFilter, setSchoolYearFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const categoryOptions = useMemo(
    () => [...new Set(projects.map((p) => p.category).filter(Boolean))].sort(),
    [projects]
  );

  const schoolYearOptions = useMemo(
    () =>
      [...new Set(projects.map((p) => getSchoolYearForDate(p.expense_date)).filter(Boolean))]
        .sort()
        .reverse(),
    [projects]
  );

  const filteredProjects = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return projects.filter((p) => {
      if (categoryFilter && p.category !== categoryFilter) return false;
      if (schoolYearFilter && getSchoolYearForDate(p.expense_date) !== schoolYearFilter) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (!q) return true;
      return [p.name, p.category, p.voucher_number, p.recipient, p.remarks]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [projects, searchTerm, categoryFilter, schoolYearFilter, statusFilter]);

  const totalDisbursed = useMemo(
    () =>
      filteredProjects
        .filter((p) => DISBURSED_STATUSES.has(p.status))
        .reduce((sum, p) => sum + Number(p.amount), 0),
    [filteredProjects]
  );

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredProjects.slice(start, start + PAGE_SIZE);
  }, [filteredProjects, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, categoryFilter, schoolYearFilter, statusFilter]);

  const hasActiveFilters = Boolean(searchTerm || categoryFilter || schoolYearFilter || statusFilter);

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setSchoolYearFilter('');
    setStatusFilter('');
  };

  const handleDelete = async (project) => {
    const confirmed = await confirmAction({
      title: 'Delete project?',
      text: `Delete "${project.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!confirmed) return;
    try {
      await remove(project.id, project);
    } catch (err) {
      showAlert({ title: 'Failed to delete project', text: err.message || 'Failed to delete project' });
    }
  };

  const handleSubmit = (formData) => {
    if (modalState && modalState !== 'new') {
      return update(modalState.id, formData, modalState);
    }
    return create(formData);
  };

  return (
    <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 sm:px-5 py-4 bg-navy-light space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Disbursements</h2>
            <p className="text-sm text-gray-200">Disbursements and expenses</p>
          </div>
          <button
            type="button"
            onClick={() => setModalState('new')}
            className="flex items-center justify-center gap-1.5 rounded-md bg-gold text-navy text-lg font-medium px-3 py-2 hover:bg-gold-dark"
          >
            <PlusIcon className="h-4 w-4" />
            Add Project
          </button>
        </div>

        <div className="flex items-center gap-2 w-full">
          <div className="relative flex-1 min-w-0">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, category, voucher #, recipient…"
              className="w-full rounded-md border-0 pl-9 pr-3 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            aria-label="Toggle filters"
            aria-pressed={showFilters}
            className={`shrink-0 p-2.5 rounded-md border transition ${
              showFilters
                ? 'bg-white text-navy border-white'
                : 'border-gray-300 text-gray-200 hover:text-white hover:border-white'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
          </button>
        </div>

        {showFilters && (
          <div className="flex flex-wrap items-center gap-2 w-full">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Categories</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Statuses</option>
              <option value="Planned">Planned</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Completed">Completed</option>
            </select>

            <select
              value={schoolYearFilter}
              onChange={(e) => setSchoolYearFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All School Years</option>
              {schoolYearOptions.map((sy) => (
                <option key={sy} value={sy}>
                  {sy}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className="w-full sm:w-auto text-lg text-gray-200 border border-gray-300 px-3 py-2 rounded-md hover:text-white whitespace-nowrap disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed disabled:hover:text-gray-200"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {!loading && !error && filteredProjects.length > 0 && (
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          totalItems={filteredProjects.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      {loading ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">Loading disbursements…</p>
      ) : error ? (
        <p className="px-5 py-8 text-sm text-danger text-center">{error}</p>
      ) : filteredProjects.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">
          {projects.length === 0 ? 'No disbursements recorded' : 'No disbursements match your search/filters'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200 text-md text-gray-500 uppercase">
                <th className="py-2 pl-5 pr-4 font-semibold whitespace-nowrap">Name</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Category</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Amount</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Date</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Voucher/OR #</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Recipient</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Status</th>
                <th className="py-2 pr-5 font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedProjects.map((project) => (
                <tr key={project.id} className="hover:bg-gold/10">
                  <td className="py-3 pl-5 pr-4 font-medium text-gray-900 whitespace-nowrap">{project.name}</td>
                  <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{project.category || '—'}</td>
                  <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{peso(project.amount)}</td>
                  <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{project.expense_date}</td>
                  <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{project.voucher_number || '—'}</td>
                  <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{project.recipient || '—'}</td>
                  <td className="py-3 pr-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        statusStyles[project.status] ?? 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {project.status}
                    </span>
                  </td>
                  <td className="py-3 pr-5 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setModalState(project)}
                        className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
                        aria-label="Edit project"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(project)}
                          className="p-1.5 rounded-md text-danger hover:bg-danger/10"
                          aria-label="Delete project"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && filteredProjects.length > 0 && (
        <div className="flex justify-between items-center mx-3 sm:mx-5 mb-4 px-4 py-3 rounded-lg text-xl sm:text-2xl font-bold bg-danger/10 text-danger">
          <span>Total Disbursed</span>
          <span>{peso(totalDisbursed)}</span>
        </div>
      )}

      {modalState && (
        <ProjectFormModal
          project={modalState === 'new' ? null : modalState}
          onClose={() => setModalState(null)}
          onSubmit={handleSubmit}
        />
      )}
    </section>
  );
}

export default ProjectsPage;
